// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CrmRelatorios.tsx
//  CRM — Relatórios analíticos (vendedor, origem, evolução, motivos de perda)
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  FileBarChart, Download, Trophy, XCircle, Activity, TrendingUp, Users,
} from 'lucide-react';
import { useCrmDashboard } from '@/features/operacional/hooks/useCrmDashboard';
import type {
  DesempenhoVendedorComNome, FiltrosCrmDashboard,
} from '@/features/operacional/services/crmDashboardService';
import { useSalespeople } from '@/features/pessoas/hooks/useSalespeople';
import { BarraFiltros } from '@/components/BarraFiltros';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const fmt    = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v: number) => `${v.toFixed(1)}%`;
const MESES_NOMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function exportCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const csv = [header, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const CrmRelatoriosPage = () => {
  // FrmFiltroCrm: período, vendedor e cliente. O período recorta oportunidades pela
  // criação; "meses" controla só a série da evolução mensal.
  const [meses, setMeses] = useState(12);
  const [filtros, setFiltros] = useState<FiltrosCrmDashboard>({});
  const { data, isLoading } = useCrmDashboard(meses, 1, filtros);
  const { salespeople } = useSalespeople();
  const filtrosAtivos = [!!filtros.vendedorId, !!filtros.clienteId, !!(filtros.de || filtros.ate)].filter(Boolean).length;
  // Agregado no servidor: só oportunidades PERDIDAS, de todas (não das 100 primeiras).
  const motivosPerda = (data?.motivosPerda ?? [])
    .map((m) => ({ motivo: m.motivo, qtd: m.quantidade, valor: m.valor }));

  if (isLoading || !data) {
    return <div className="p-6 space-y-4"><Skeleton className="h-12" /><Skeleton className="h-64" /></div>;
  }

  const { origens, evolucaoGanhos, kpis } = data;
  const vendedores = data.vendedores as DesempenhoVendedorComNome[];
  const nomeVendedor = (v: DesempenhoVendedorComNome) => v.vendedorNome ?? '(vendedor não encontrado)';

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <FileBarChart className="h-7 w-7 text-primary" /> Relatórios CRM
        </h1>
        <p className="text-muted-foreground">Análises consolidadas para tomada de decisão</p>
      </div>

      <BarraFiltros filtrosAtivos={filtrosAtivos} onLimpar={() => setFiltros({})}>
        <FiltroPeriodo rotulo="Criação / solicitação" inicio={filtros.de ?? ''} fim={filtros.ate ?? ''}
          onChange={(p) => setFiltros((f) => ({ ...f, de: p.inicio || undefined, ate: p.fim || undefined }))} />
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Vendedor</Label>
          <Select value={filtros.vendedorId ? String(filtros.vendedorId) : '__todos__'}
            onValueChange={(v) => setFiltros((f) => ({ ...f, vendedorId: v === '__todos__' ? undefined : Number(v) }))}>
            <SelectTrigger className="h-9 w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__todos__">Todos os vendedores</SelectItem>
              {salespeople.map((v) => (
                <SelectItem key={v.id} value={String(v.id)}>{v.apelido || v.nomeRazao}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[260px] space-y-1">
          <Label className="text-xs text-muted-foreground">Cliente</Label>
          <PessoaSearchCombobox value={filtros.clienteId} filter="cliente" placeholder="Todos os clientes"
            onChange={(id) => setFiltros((f) => ({ ...f, clienteId: id || undefined }))} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Evolução mensal</Label>
          <Select value={String(meses)} onValueChange={(v) => setMeses(Number(v))}>
            <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[3, 6, 12, 24, 36].map((m) => <SelectItem key={m} value={String(m)}>Últimos {m} meses</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </BarraFiltros>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <ResumoCard icon={<Trophy className="h-4 w-4 text-emerald-500" />} title="Ganhos no mês"
          value={fmt(kpis.valorGanhoMes)} />
        <ResumoCard icon={<TrendingUp className="h-4 w-4 text-blue-500" />} title="Conversão"
          value={fmtPct(kpis.taxaConversao)} />
        <ResumoCard icon={<Activity className="h-4 w-4 text-amber-500" />} title="Ciclo médio"
          value={`${kpis.cicloVendaDias}d`} />
        <ResumoCard icon={<Users className="h-4 w-4 text-cyan-500" />} title="Pipeline aberto"
          value={fmt(kpis.valorPipelineAberto)} />
      </div>

      <Tabs defaultValue="vendedores">
        <TabsList>
          <TabsTrigger value="vendedores">Vendedores</TabsTrigger>
          <TabsTrigger value="origens">Origens</TabsTrigger>
          <TabsTrigger value="evolucao">Evolução mensal</TabsTrigger>
          <TabsTrigger value="perdas">Motivos de perda</TabsTrigger>
        </TabsList>

        <TabsContent value="vendedores">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Performance por vendedor</CardTitle>
              <Button variant="outline" size="sm" className="gap-2"
                onClick={() => exportCsv(
                  'crm-vendedores.csv',
                  ['Vendedor', 'Abertas', 'Ganhas', 'Perdidas', 'Valor ganho', 'Pipeline', 'Conversão'],
                  vendedores.map((v) => [
                    nomeVendedor(v), v.oportunidadesAbertas, v.oportunidadesGanhas,
                    v.oportunidadesPerdidas, v.valorGanho.toFixed(2),
                    v.valorPipeline.toFixed(2), v.taxaConversao.toFixed(2) + '%',
                  ]),
                )}>
                <Download className="h-4 w-4" /> Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-center">Abertas</TableHead>
                    <TableHead className="text-center">Ganhas</TableHead>
                    <TableHead className="text-center">Perdidas</TableHead>
                    <TableHead className="text-right">Valor ganho</TableHead>
                    <TableHead className="text-right">Pipeline</TableHead>
                    <TableHead className="text-right">Conversão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendedores.map((v) => (
                    <TableRow key={v.vendedorId}>
                      <TableCell className="font-medium">{nomeVendedor(v)}</TableCell>
                      <TableCell className="text-center">{v.oportunidadesAbertas}</TableCell>
                      <TableCell className="text-center text-emerald-600 font-medium">{v.oportunidadesGanhas}</TableCell>
                      <TableCell className="text-center text-rose-600">{v.oportunidadesPerdidas}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(v.valorGanho)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(v.valorPipeline)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">{fmtPct(v.taxaConversao)}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="origens">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Análise por origem do lead</CardTitle>
              <Button variant="outline" size="sm" className="gap-2"
                onClick={() => exportCsv(
                  'crm-origens.csv',
                  ['Origem', 'Quantidade', 'Valor', 'Conversão'],
                  origens.map((o) => [o.origem, o.quantidade, o.valor.toFixed(2), o.taxaConversao.toFixed(2) + '%']),
                )}>
                <Download className="h-4 w-4" /> Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Origem</TableHead>
                    <TableHead className="text-center">Quantidade</TableHead>
                    <TableHead className="text-right">Valor total</TableHead>
                    <TableHead className="text-right">Conversão</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {origens.map((o) => (
                    <TableRow key={o.origem}>
                      <TableCell className="font-medium">{o.origem}</TableCell>
                      <TableCell className="text-center">{o.quantidade}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(o.valor)}</TableCell>
                      <TableCell className="text-right">{fmtPct(o.taxaConversao)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evolucao">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Evolução mensal de ganhos ({meses}m)</CardTitle>
              <Button variant="outline" size="sm" className="gap-2"
                onClick={() => exportCsv(
                  'crm-evolucao.csv',
                  ['Ano', 'Mês', 'Quantidade', 'Valor'],
                  evolucaoGanhos.map((e) => [e.ano, e.mes, e.quantidade, e.valor.toFixed(2)]),
                )}>
                <Download className="h-4 w-4" /> Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Período</TableHead>
                    <TableHead className="text-center">Oportunidades ganhas</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evolucaoGanhos.map((e) => (
                    <TableRow key={`${e.ano}-${e.mes}`}>
                      <TableCell className="font-medium">{MESES_NOMES[e.mes - 1]}/{e.ano}</TableCell>
                      <TableCell className="text-center">{e.quantidade}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(e.valor)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="perdas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-rose-500" /> Motivos de perda
              </CardTitle>
              <Button variant="outline" size="sm" className="gap-2"
                onClick={() => exportCsv(
                  'crm-motivos-perda.csv',
                  ['Motivo', 'Quantidade', 'Valor'],
                  motivosPerda.map((m) => [m.motivo, m.qtd, m.valor.toFixed(2)]),
                )}>
                <Download className="h-4 w-4" /> Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              {motivosPerda.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma oportunidade perdida no período.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Motivo</TableHead>
                      <TableHead className="text-center">Quantidade</TableHead>
                      <TableHead className="text-right">Valor envolvido</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {motivosPerda.map((m) => (
                      <TableRow key={m.motivo}>
                        <TableCell className="font-medium">{m.motivo}</TableCell>
                        <TableCell className="text-center">{m.qtd}</TableCell>
                        <TableCell className="text-right font-mono">{fmt(m.valor)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ResumoCard = ({ icon, title, value }: { icon: React.ReactNode; title: string; value: string }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-2">{icon} {title}</CardTitle>
    </CardHeader>
    <CardContent><div className="text-2xl font-bold">{value}</div></CardContent>
  </Card>
);

export default CrmRelatoriosPage;
